import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@lib/queryKeys';
import {
  psGetAll,
  usePowerSyncChange,
} from '@/renderer/lib/powersync/ipcBridge';
import { useSyncStatus } from '@hooks/useSyncStatus';
import { fetchOutletCategories } from '@/renderer/services/menuService';
import { MenuCategory } from '@/renderer/types/menu';

export function useCategories(outletId?: string | null) {
  const queryClient = useQueryClient();
  const { hasSynced } = useSyncStatus();
  const prevHasSynced = useRef(hasSynced);

  // Re-fetch whenever PowerSync writes new category data from the server.
  usePowerSyncChange(['menu_categories'], () => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.menu.categories(outletId),
    });
  });

  // When PowerSync completes its very first sync, invalidate so the query
  // picks up the data that was just loaded into local SQLite. Without this,
  // if the sync finishes before the component mounts, usePowerSyncChange
  // never fires and the page stays blank (staleTime is Infinity).
  useEffect(() => {
    if (!prevHasSynced.current && hasSynced && outletId) {
      queryClient.invalidateQueries({
        queryKey: queryKeys.menu.categories(outletId),
      });
    }
    prevHasSynced.current = hasSynced;
  }, [hasSynced, outletId, queryClient]);

  return useQuery({
    queryKey: queryKeys.menu.categories(outletId),
    queryFn: async (): Promise<MenuCategory[]> => {
      if (!outletId) {
        // Company-wide view (e.g. a super user viewing "All Outlets") — local
        // PowerSync SQLite only ever holds one outlet's data at a time, so
        // fall back to a direct Supabase fetch. RLS already scopes
        // menu_categories reads to the caller's own company.
        const response = await fetchOutletCategories(null);
        if (!response.success) {
          throw new Error(response.error ?? 'Failed to fetch categories');
        }
        return response.data ?? [];
      }
      const rows = await psGetAll<any>(
        `SELECT id, name, outlet_id, description, display_order, status,
                created_at, updated_at
         FROM menu_categories
         WHERE outlet_id = ?
         ORDER BY display_order ASC, name ASC`,
        [outletId],
      );
      return rows.map((r) => ({
        id: r.id,
        outlet_id: r.outlet_id || outletId,
        name: r.name || '',
        description: r.description ?? undefined,
        display_order: Number(r.display_order) || 0,
        status: r.status || 'active',
        created_at: r.created_at || '',
        updated_at: r.updated_at || '',
      }));
    },
    // Company-wide reads aren't PowerSync-backed, so they can go stale
    // normally; the single-outlet local path stays Infinity (invalidated
    // explicitly via usePowerSyncChange above).
    staleTime: outletId ? Infinity : 1000 * 60 * 5,
  });
}
